/**
 * Typed API client.
 *
 * Base URL comes from VITE_API_BASE_URL and defaults to empty, meaning
 * same-origin /api. That is the shape with a Static Web Apps linked backend and
 * with the Vite dev proxy. Setting it to the App Service origin is all that is
 * needed for the Free-tier layout where the two are on different hosts.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * The whole response body. Almost every failure needs only the message, but
     * a rejected consultation form also carries a message per question, and
     * those have to reach the field they belong to.
     */
    readonly payload: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Per-question errors from a rejected form submission, if there are any. */
  fieldErrors(): ConsultationFieldError[] {
    const fields = (this.payload as { fields?: unknown } | null)?.fields;
    if (!Array.isArray(fields)) return [];
    return fields.filter(
      (entry): entry is ConsultationFieldError =>
        typeof (entry as ConsultationFieldError)?.fieldId === 'number' &&
        typeof (entry as ConsultationFieldError)?.message === 'string',
    );
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
}

async function call<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK', 'We could not reach the studio. Please check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Something went wrong. Please try again.',
      payload,
    );
  }

  return payload as T;
}

// --- Public shapes ---------------------------------------------------------

/** A live offer, as it should read beside a price. */
export interface ServiceOffer {
  label: string;
  /** "20% off", "£10.00 off". */
  descriptor: string;
  siteWide: boolean;
}

export interface Service {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  durationMinutes: number;
  /** What the client pays, with any live offer already applied. */
  pricePence: number;
  /** The menu price. Crossed out when it differs from pricePence. */
  originalPence: number;
  savingPence: number;
  offer: ServiceOffer | null;
  depositPence: number;
  /** Served by the API; null when no photograph has been uploaded. */
  imageUrl: string | null;
  imageAltText: string | null;
  /** Featured large on the homepage. At most one treatment has it, and possibly none. */
  signature: boolean;
}

export interface ServiceCategory {
  id: number;
  name: string;
  slug: string;
  blurb: string | null;
  services: Service[];
}

export interface CatalogueResponse {
  depositPercent: number;
  cancellationWindowHours: number;
  /**
   * Surfaced separately as well as applied to each price, so the page can say it
   * once in a banner rather than badge every treatment in the list.
   */
  siteWideOffer: { label: string; descriptor: string; endsOnDate: string | null } | null;
  categories: ServiceCategory[];
}

/**
 * One stretch of a day the studio is open. A weekday may have several, and a
 * weekday with none is closed - there is no separate "closed" flag.
 */
export interface OpeningWindow {
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
}

export interface OpeningWindowRecord extends OpeningWindow {
  id: number;
}

/** Editable copy, keyed `section.field`. Missing keys fall back to defaults. */
export type SiteContent = Record<string, string>;

export interface StudioResponse {
  name: string;
  address: string;
  phone: string;
  instagram: string;
  content: SiteContent;
  windows: OpeningWindowRecord[];
  depositPercent: number;
  cancellationWindowHours: number;
  maxDaysAhead: number;
}

export interface Slot {
  startUtc: string;
  endUtc: string;
  label: string;
}

export interface AvailabilityResponse {
  date: string;
  service: {
    id: number;
    name: string;
    durationMinutes: number;
    pricePence: number;
    originalPence: number;
    savingPence: number;
    offerLabel: string | null;
  };
  slots: Slot[];
}

export interface MonthAvailabilityResponse {
  month: string;
  availableDates: string[];
}

export interface CreateBookingResponse {
  reference: string;
  manageToken: string;
  depositPence: number;
  balancePence: number;
  holdExpiresUtc: string | null;
  checkoutUrl: string;
}

export interface PublicBooking {
  reference: string;
  status: string;
  serviceName: string;
  durationMinutes: number;
  startUtc: string;
  endUtc: string;
  dateLabel: string;
  timeLabel: string;
  pricePence: number;
  depositPence: number;
  balancePence: number;
  paid: boolean;
  refundPence: number | null;
  customerName: string;
  customerEmail: string;
  cancellable: boolean;
  /** What would come back if they cancelled at the moment of this response. */
  refundIfCancelledNow: number;
  /** That figure said in words, for the page and the confirmation prompt. */
  cancellationOutlook: string;
  /** Notice the studio asks for, so the page can name the policy. */
  cancellationWindowHours: number;
  /** The last instant at which cancelling still returns the deposit. */
  freeCancellationUntilUtc: string;
  /** What became of the deposit, once the booking is cancelled. */
  refundSummary: string | null;
  /** What came off, and the offer it came from. Null when there was none. */
  discountLabel: string | null;
  discountPence: number | null;
  message?: string;
  /**
   * Whether their consultation form is outstanding. Absent from the cancellation
   * response, which does not need it.
   */
  consultation?: {
    /** Zero when the studio has not put any questions on the form. */
    questionCount: number;
    submitted: boolean;
    submittedUtc: string | null;
  };
}

// --- Consultation form -----------------------------------------------------

/**
 * What a question can be.
 *
 * `heading` and `statement` are read rather than answered; everything else
 * collects something. Kept in step with FIELD_KINDS in the API by the form
 * builder round-tripping the value it was given.
 */
export type ConsultationFieldKind =
  | 'heading'
  | 'statement'
  | 'text'
  | 'textarea'
  | 'email'
  | 'tel'
  | 'date'
  | 'number'
  | 'yesno'
  | 'choice'
  | 'multi'
  | 'scale'
  | 'consent';

export interface ConsultationField {
  id: number;
  kind: ConsultationFieldKind;
  label: string;
  helpText: string | null;
  /** Choices for choice and multi; the two end labels for scale. */
  options: string[];
  isRequired: boolean;
  sortOrder: number;
}

export interface ConsultationFieldError {
  fieldId: number;
  message: string;
}

/** Answers come back as arrays whatever the kind, so hydration is uniform. */
export interface ConsultationAnswerValues {
  fieldId: number;
  values: string[];
}

export interface ConsultationPage {
  booking: {
    reference: string;
    status: string;
    serviceName: string;
    startUtc: string;
    dateLabel: string;
    timeLabel: string;
    customerName: string;
  };
  form: {
    title: string;
    intro: string | null;
    fields: ConsultationField[];
  };
  submission: {
    submittedUtc: string;
    updatedUtc: string | null;
    answers: ConsultationAnswerValues[];
  } | null;
  editable: boolean;
  closedReason: string | null;
  message?: string;
}

// --- Admin shapes ----------------------------------------------------------

export interface AdminBooking {
  id: number;
  reference: string;
  status: string;
  source: string;
  serviceName: string;
  durationMinutes: number;
  startUtc: string;
  endUtc: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string | null;
  notes: string | null;
  pricePence: number;
  depositPence: number;
  discountLabel: string | null;
  discountPence: number | null;
  paid: boolean;
  refundPence: number | null;
  refunded: boolean;
  cancelledBy: string | null;
  createdUtc: string;
  /** Start minus the before-buffer: when the room is actually committed. */
  occupiedFromUtc: string;
  /** End plus the after-buffer: when the room is free again. */
  occupiedToUtc: string;
  /** Whether their consultation form is in. */
  consultationSubmitted: boolean;
}

export interface AdminService {
  id: number;
  categoryId: number;
  categoryName: string;
  name: string;
  slug: string;
  description: string | null;
  durationMinutes: number;
  pricePence: number;
  isActive: boolean;
  /** The one treatment featured on the homepage. Ticking another clears it. */
  isSignature: boolean;
  sortOrder: number;
  priceLabel: string;
  /** Today's price while an offer runs, or null when none applies. */
  offerPricePence: number | null;
  offerLabel: string | null;
  offerDescriptor: string | null;
  depositPence: number;
  hasImage: boolean;
  imageUrl: string | null;
  imageAltText: string | null;
  /** How many bookings reference this treatment, ever. */
  bookingCount: number;
  /** False once anything has been booked against it - deactivate instead. */
  deletable: boolean;
}

export interface ServiceCategoryRecord {
  id: number;
  name: string;
  slug: string;
  blurb: string | null;
  sortOrder: number;
}

export interface AdminCategory extends ServiceCategoryRecord {
  treatmentCount: number;
  /** False while treatments still sit in it. */
  deletable: boolean;
}

export interface ContentField {
  key: string;
  value: string;
  label: string;
  hint: string | null;
  section: string;
  sortOrder: number;
  isLong: boolean;
  /** May be saved blank, and is then left off the page rather than refused. */
  optional: boolean;
}

export interface NewServiceInput {
  categoryId: number;
  name: string;
  description?: string;
  durationMinutes: number;
  pricePence: number;
  isActive?: boolean;
}

export interface ServiceChanges {
  categoryId?: number;
  name?: string;
  description?: string | null;
  durationMinutes?: number;
  pricePence?: number;
  isActive?: boolean;
  /** True takes the signature from whichever treatment had it. */
  isSignature?: boolean;
  sortOrder?: number;
}

export interface Blackout {
  id: number;
  startUtc: string;
  endUtc: string;
  reason: string | null;
}

export interface StudioSettings {
  slotGranularityMinutes: number;
  /** Consultation and early arrival, reserved before each appointment. */
  bufferBeforeMinutes: number;
  /** Payment and room turnaround, reserved after each appointment. */
  bufferAfterMinutes: number;
  minLeadTimeMinutes: number;
  maxDaysAhead: number;
  depositPercent: number;
  cancellationWindowHours: number;
  holdMinutes: number;
}

export interface AdminConsultationForm {
  title: string;
  intro: string | null;
  updatedUtc: string;
  /** Headings and statements do not count. */
  questionCount: number;
  fields: ConsultationField[];
}

/** A field on its way back to the API. No id means a new question. */
export interface ConsultationFieldInput {
  id?: number;
  kind: ConsultationFieldKind;
  label: string;
  helpText: string | null;
  options: string[];
  isRequired: boolean;
}

export interface ConsultationRow {
  bookingId: number;
  reference: string;
  status: string;
  serviceName: string;
  startUtc: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string | null;
  /** False when there is no usable email address, so nothing can be sent. */
  contactable: boolean;
  submitted: boolean;
  submittedUtc: string | null;
  updatedUtc: string | null;
  answerCount: number;
  lastNudgeUtc: string | null;
  nudgeCount: number;
  consultationUrl: string;
}

export interface ConsultationListResponse {
  from: string;
  to: string;
  questionCount: number;
  formTitle: string;
  /** False when no mail transport is set up, so the UI can say why. */
  emailConfigured: boolean;
  total: number;
  awaiting: number;
  received: number;
  bookings: ConsultationRow[];
}

export interface SubmittedAnswerView {
  label: string;
  kind: ConsultationFieldKind;
  values: string[];
  /** False once the question it was filed against has been deleted. */
  live: boolean;
}

export interface EmailLogView {
  id: number;
  kind: 'confirmation' | 'cancellation' | 'nudge' | 'reminder';
  subject: string;
  recipient: string;
  status: 'sending' | 'sent' | 'failed';
  error: string | null;
  createdUtc: string;
}

export interface ConsultationDetail {
  booking: {
    id: number;
    reference: string;
    status: string;
    serviceName: string;
    startUtc: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string | null;
    notes: string | null;
    contactable: boolean;
  };
  consultationUrl: string;
  submission: {
    formTitle: string;
    submittedUtc: string;
    updatedUtc: string | null;
    answers: SubmittedAnswerView[];
  } | null;
  emails: EmailLogView[];
}

export interface EmailActionResult {
  reference: string;
  status: 'sent' | 'duplicate' | 'skipped' | 'failed';
  message: string;
}

export interface ReminderRunResult {
  windowFromUtc: string;
  windowToUtc: string;
  considered: number;
  sent: number;
  skipped: number;
  failed: number;
  lines: string[];
}

export type DiscountKind = 'percent' | 'amount';

export interface AdminDiscount {
  id: number;
  label: string;
  serviceId: number | null;
  serviceName: string | null;
  siteWide: boolean;
  kind: DiscountKind;
  /** Percent for a percentage offer, pence for an amount off. */
  value: number;
  descriptor: string;
  startUtc: string;
  endUtc: string | null;
  /** The dates as typed, so the form round-trips without shifting them. */
  startDate: string;
  startTime: string | null;
  /** The last day the offer runs, inclusive. */
  endDate: string | null;
  endTime: string | null;
  isActive: boolean;
  /** Running right now - the only state that changes a price. */
  live: boolean;
  scheduled: boolean;
  finished: boolean;
}

/** What a treatment costs today, so the studio can see an offer landing. */
export interface OfferPreviewRow {
  id: number;
  name: string;
  originalPence: number;
  pricePence: number;
  savingPence: number;
  depositPence: number;
  offerLabel: string | null;
}

export interface DiscountsResponse {
  maxPercent: number;
  minimumChargePence: number;
  discounts: AdminDiscount[];
  treatments: OfferPreviewRow[];
}

export interface DiscountInput {
  label: string;
  serviceId: number | null;
  kind: DiscountKind;
  /** Percent for a percentage offer, pounds for an amount off. */
  value: number;
  startDate: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  isActive?: boolean;
}

// --- Endpoints -------------------------------------------------------------

export const api = {
  getServices: (signal?: AbortSignal) =>
    call<CatalogueResponse>('/api/services', { signal }),

  getStudio: (signal?: AbortSignal) => call<StudioResponse>('/api/studio', { signal }),

  getAvailability: (serviceId: number, date: string, signal?: AbortSignal) =>
    call<AvailabilityResponse>(
      `/api/availability?serviceId=${serviceId}&date=${encodeURIComponent(date)}`,
      { signal },
    ),

  getMonthAvailability: (serviceId: number, month: string, signal?: AbortSignal) =>
    call<MonthAvailabilityResponse>(
      `/api/availability/month?serviceId=${serviceId}&month=${encodeURIComponent(month)}`,
      { signal },
    ),

  createBooking: (body: {
    serviceId: number;
    startUtc: string;
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    notes?: string;
  }) => call<CreateBookingResponse>('/api/bookings', { method: 'POST', body }),

  getBooking: (reference: string, token: string, signal?: AbortSignal) =>
    call<PublicBooking>(
      `/api/bookings/${encodeURIComponent(reference)}?t=${encodeURIComponent(token)}`,
      { signal },
    ),

  cancelBooking: (reference: string, token: string) =>
    call<PublicBooking>(
      `/api/bookings/${encodeURIComponent(reference)}/cancel?t=${encodeURIComponent(token)}`,
      { method: 'POST' },
    ),

  getConsultation: (reference: string, token: string, signal?: AbortSignal) =>
    call<ConsultationPage>(
      `/api/consultation/${encodeURIComponent(reference)}?t=${encodeURIComponent(token)}`,
      { signal },
    ),

  submitConsultation: (
    reference: string,
    token: string,
    answers: Array<{ fieldId: number; value: string | string[] }>,
  ) =>
    call<ConsultationPage>(
      `/api/consultation/${encodeURIComponent(reference)}?t=${encodeURIComponent(token)}`,
      { method: 'POST', body: { answers } },
    ),

  admin: {
    login: (password: string) =>
      call<{ token: string; expiresInSeconds: number }>('/api/admin/login', {
        method: 'POST',
        body: { password },
      }),

    checkSession: (token: string) => call<{ ok: boolean }>('/api/admin/session', { token }),

    getBookings: (token: string, from: string, to: string, signal?: AbortSignal) =>
      call<{
        from: string;
        to: string;
        bufferBeforeMinutes: number;
        bufferAfterMinutes: number;
        bookings: AdminBooking[];
      }>(`/api/admin/bookings?from=${from}&to=${to}`, { token, signal }),

    createBooking: (
      token: string,
      body: {
        serviceId: number;
        date: string;
        time: string;
        customerName: string;
        customerEmail?: string;
        customerPhone?: string;
        notes?: string;
      },
    ) => call<{ id: number; reference: string }>('/api/admin/bookings', { method: 'POST', body, token }),

    cancelBooking: (token: string, id: number, forceRefund: boolean) =>
      call<{ reference: string; refundPence: number; message: string }>(
        `/api/admin/bookings/${id}/cancel`,
        { method: 'POST', body: { forceRefund }, token },
      ),

    refundBooking: (token: string, id: number) =>
      call<{ reference: string; refundPence: number; message: string }>(
        `/api/admin/bookings/${id}/refund`,
        { method: 'POST', body: {}, token },
      ),

    getHours: (token: string) =>
      call<{ windows: OpeningWindowRecord[] }>('/api/admin/hours', { token }),

    saveHours: (token: string, windows: OpeningWindow[]) =>
      call<{ windows: OpeningWindowRecord[] }>('/api/admin/hours', {
        method: 'PUT',
        body: { windows },
        token,
      }),

    getBlackouts: (token: string) =>
      call<{ blackouts: Blackout[] }>('/api/admin/blackouts', { token }),

    createBlackout: (
      token: string,
      body: { startDate: string; endDate: string; startTime?: string; endTime?: string; reason?: string },
    ) => call<Blackout>('/api/admin/blackouts', { method: 'POST', body, token }),

    deleteBlackout: (token: string, id: number) =>
      call<{ deleted: number }>(`/api/admin/blackouts/${id}`, { method: 'DELETE', token }),

    getServices: (token: string) =>
      call<{
        depositPercent: number;
        /** False when AZURE_STORAGE_ACCOUNT is unset, so the UI can say why. */
        imageStoreConfigured: boolean;
        categories: ServiceCategoryRecord[];
        services: AdminService[];
      }>('/api/admin/services', { token }),

    createService: (token: string, body: NewServiceInput) =>
      call<AdminService>('/api/admin/services', { method: 'POST', body, token }),

    updateService: (token: string, id: number, changes: ServiceChanges) =>
      call<AdminService>(`/api/admin/services/${id}`, { method: 'PUT', body: changes, token }),

    deleteService: (token: string, id: number) =>
      call<{ deleted: number; name: string }>(`/api/admin/services/${id}`, {
        method: 'DELETE',
        token,
      }),

    /**
     * Uploads a prepared image as raw bytes.
     *
     * Not routed through `call`, which assumes a JSON body - the API takes the
     * image bytes directly with a real Content-Type, so there is no multipart
     * encoding on either side.
     */
    uploadServiceImage: async (
      token: string,
      id: number,
      blob: Blob,
      contentType: string,
      altText: string,
    ) => {
      let response: Response;
      try {
        response = await fetch(`${BASE}/api/admin/services/${id}/image`, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
            'X-Image-Alt': altText.replace(/[^\t\x20-\x7e]/g, ''),
            Authorization: `Bearer ${token}`,
          },
          body: blob,
        });
      } catch {
        throw new ApiError(0, 'NETWORK', 'The upload could not reach the studio.');
      }

      const text = await response.text();
      const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
      if (!response.ok) {
        const error = payload.error as { code?: string; message?: string } | undefined;
        throw new ApiError(
          response.status,
          error?.code ?? 'UNKNOWN',
          error?.message ?? 'The image could not be uploaded.',
        );
      }
      return payload as unknown as {
        id: number;
        bytes: number;
        contentType: string;
        imageAltText: string;
        imageUrl: string;
      };
    },

    deleteServiceImage: (token: string, id: number) =>
      call<{ id: number; removed: boolean }>(`/api/admin/services/${id}/image`, {
        method: 'DELETE',
        token,
      }),

    updateServiceImageAlt: (token: string, id: number, altText: string) =>
      call<{ id: number; imageAltText: string | null }>(`/api/admin/services/${id}/image/alt`, {
        method: 'PUT',
        body: { altText },
        token,
      }),

    getCategories: (token: string) =>
      call<{ categories: AdminCategory[] }>('/api/admin/categories', { token }),

    createCategory: (token: string, body: { name: string; blurb?: string }) =>
      call<AdminCategory>('/api/admin/categories', { method: 'POST', body, token }),

    updateCategory: (
      token: string,
      id: number,
      changes: { name?: string; blurb?: string | null; sortOrder?: number },
    ) => call<ServiceCategoryRecord>(`/api/admin/categories/${id}`, {
      method: 'PUT',
      body: changes,
      token,
    }),

    deleteCategory: (token: string, id: number) =>
      call<{ deleted: number; name: string }>(`/api/admin/categories/${id}`, {
        method: 'DELETE',
        token,
      }),

    getContent: (token: string) =>
      call<{ fields: ContentField[] }>('/api/admin/content', { token }),

    saveContent: (token: string, values: Record<string, string>) =>
      call<{ applied: string[]; fields: ContentField[] }>('/api/admin/content', {
        method: 'PUT',
        body: { values },
        token,
      }),

    getSettings: (token: string) => call<StudioSettings>('/api/admin/settings', { token }),

    saveSettings: (token: string, changes: Partial<StudioSettings>) =>
      call<StudioSettings>('/api/admin/settings', { method: 'PUT', body: changes, token }),

    getDiscounts: (token: string) =>
      call<DiscountsResponse>('/api/admin/discounts', { token }),

    createDiscount: (token: string, body: DiscountInput) =>
      call<AdminDiscount>('/api/admin/discounts', { method: 'POST', body, token }),

    updateDiscount: (token: string, id: number, body: DiscountInput) =>
      call<AdminDiscount>(`/api/admin/discounts/${id}`, { method: 'PUT', body, token }),

    deleteDiscount: (token: string, id: number) =>
      call<{ deleted: number; label: string }>(`/api/admin/discounts/${id}`, {
        method: 'DELETE',
        token,
      }),

    getConsultationForm: (token: string) =>
      call<AdminConsultationForm>('/api/admin/consultation-form', { token }),

    saveConsultationForm: (
      token: string,
      body: { title: string; intro: string | null; fields: ConsultationFieldInput[] },
    ) => call<AdminConsultationForm>('/api/admin/consultation-form', {
      method: 'PUT',
      body,
      token,
    }),

    getConsultations: (token: string, from?: string, to?: string, signal?: AbortSignal) => {
      const query = new URLSearchParams();
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const suffix = query.toString();
      return call<ConsultationListResponse>(
        `/api/admin/consultations${suffix ? `?${suffix}` : ''}`,
        { token, signal },
      );
    },

    getConsultation: (token: string, bookingId: number, signal?: AbortSignal) =>
      call<ConsultationDetail>(`/api/admin/consultations/${bookingId}`, { token, signal }),

    nudgeConsultation: (token: string, bookingId: number) =>
      call<EmailActionResult>(`/api/admin/consultations/${bookingId}/nudge`, {
        method: 'POST',
        body: {},
        token,
      }),

    resendConfirmation: (token: string, bookingId: number) =>
      call<EmailActionResult>(`/api/admin/bookings/${bookingId}/resend-confirmation`, {
        method: 'POST',
        body: {},
        token,
      }),

    /**
     * The same run the scheduler triggers, so the button is a genuine test of
     * the scheduled path rather than a parallel one. Not under /api/admin
     * because cron also has to reach it, with its own secret.
     */
    runReminders: (token: string) =>
      call<ReminderRunResult>('/api/jobs/reminders', { method: 'POST', body: {}, token }),
  },
};
